<template>
  <div class="container py-6 md:py-8">
    <fade-transition mode="out-in">
      <div
        v-if="isFinished"
        key="finishedForm"
        class="mx-auto md:w-2/3 bg-paper p-6 md:p-8 text-center">
        <p class="typeset-2 mb-4">
          {{ t("business-enquiries.success.heading") }}
        </p>
        <p class="typeset-9 mb-5">
          {{ t("business-enquiries.success.subheading") }}
        </p>
        <a
          :href="enquiryPath"
          class="btn btn-secondary btn-large">
          {{ t("business-enquiries.success.button") }}
        </a>
      </div>
      <div
        v-else
        key="unfinishedForm"
        class="mx-auto md:w-5/6">
        <header class="mb-6 md:mb-7">
          <h1 class="typeset-2 md:typeset-1 mb-4">
            {{ t("business-enquiries.heading") }}
          </h1>
          <p class="typeset-9 mb-4">
            {{ t("business-enquiries.intro") }}
          </p>
          <p class="typeset-6">
            {{ t("business-enquiries.response-time") }}
          </p>
        </header>

        <section class="mb-6 md:mb-7">
          <h2 class="typeset-4 pb-3 mb-5 border-b border-ink-1">
            {{ t("business-enquiries.form.sections.order") }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <select-input
              v-model="params.product"
              :label="t('business-enquiries.form.labels.product')"
              :options="productOptions"
              :errors="errors.product"
              :placeholder="t('business-enquiries.form.options.placeholder')"
              content-key="content"
              value-key="value"
              include-blank
              required />
            <select-input
              v-model="params.personalisation"
              :label="t('business-enquiries.form.labels.personalisation')"
              :options="personalisationOptions"
              :errors="errors.personalisation"
              :placeholder="t('business-enquiries.form.options.placeholder')"
              content-key="content"
              value-key="value"
              include-blank
              required />
            <text-input
              v-model="params.quantity"
              :label="t('business-enquiries.form.labels.quantity')"
              :placeholder="t('business-enquiries.form.labels.quantity')"
              :errors="errors.quantity"
              input-id="quantity"
              required />
            <select-input
              v-model="params.delivery_country"
              :options="countries"
              :label="t('business-enquiries.form.labels.delivery_country')"
              :errors="errors.delivery_country"
              required
              icon-key="flag_image"
              value-key="iso"
              content-key="name" />
            <date-picker
              v-model="params.delivery_date"
              :label="t('business-enquiries.form.labels.delivery_date')"
              :lang="locale"
              :disabled-date="notBeforeToday"
              :errors="errors.delivery_date"
              :add-bottom-margin="false"
              input-id="delivery_date" />
            <textarea-input
              v-model="params.urls"
              :label="t('business-enquiries.form.labels.urls')"
              :errors="errors.urls"
              class="md:col-span-full"
              input-id="urls" />
          </div>
        </section>

        <section class="mb-6 md:mb-7">
          <h2 class="typeset-4 pb-3 mb-5 border-b border-ink-1">
            {{ t("business-enquiries.form.sections.about-you") }}
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <text-input
              v-model="params.company_name"
              :label="t('business-enquiries.form.labels.company_name')"
              :placeholder="t('business-enquiries.form.labels.company_name')"
              :errors="errors.company_name"
              input-id="company_name"
              required />
            <select-input
              v-model="params.source"
              :label="t('business-enquiries.form.labels.source')"
              :options="sourceOptions"
              :errors="errors.source"
              :placeholder="t('business-enquiries.form.options.placeholder')"
              content-key="content"
              value-key="value"
              include-blank
              required />
            <create-user-form
              v-if="!loggedIn"
              class="md:col-span-full"
              :value="user"
              :errors="userErrors"
              :existing-account="existingAccount"
              :forgot-password-path="forgotPasswordPath"
              @check-email="checkEmail" />
          </div>
        </section>

        <btn
          :promise="submit()"
          type="primary"
          class="btn-large w-full md:w-auto">
          {{ t("business-enquiries.form.labels.submit") }}
        </btn>
      </div>
    </fade-transition>
  </div>
</template>

<script>
import businessApi from "../api/business";
import userApi from "../api/user";

import TextInput from "../components/shared/TextInput.vue";
import TextareaInput from "../components/shared/TextareaInput.vue";
import DatePicker from "../components/shared/DatePicker.vue";
import SelectInput from "../components/shared/SelectInput.vue";
import Btn from "../components/shared/Btn.vue";
import CreateUserForm from "../components/business/CreateUserForm.vue";
import FadeTransition from "../components/transitions/FadeTransition.vue";

export default {
  name: "Business",
  components: {
    TextInput,
    TextareaInput,
    DatePicker,
    SelectInput,
    Btn,
    CreateUserForm,
    FadeTransition
  },
  props: {
    countries: {
      type: Array,
      required: true
    },
    productOptions: {
      type: Array,
      required: true
    },
    personalisationOptions: {
      type: Array,
      required: true
    },
    sourceOptions: {
      type: Array,
      required: true
    },
    alreadyLoggedIn: {
      type: Boolean,
      required: true
    },
    defaultCountry: {
      type: String,
      required: true
    },
    locale: {
      type: String,
      required: true
    },
    enquiryPath: {
      type: String,
      required: true
    },
    forgotPasswordPath: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      params: {
        product: "",
        personalisation: "",
        quantity: null,
        urls: null,
        delivery_date: null,
        delivery_country: this.defaultCountry,
        source: "",
        company_name: null
      },
      errors: {},
      user: {
        first_name: null,
        last_name: null,
        email: null,
        password: null,
        newsletter: false
      },
      userErrors: {},
      existingAccount: false,
      isFinished: false,
      loggedIn: this.alreadyLoggedIn
    };
  },
  computed: {
    signInParams() {
      return { email: this.user.email, password: this.user.password };
    },
    notBeforeToday() {
      return date => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date.getTime() < today.getTime();
      };
    }
  },
  methods: {
    submit() {
      return () =>
        new Promise((resolve, reject) => {
          if (!this.loggedIn) {
            this.signInOrRegister().then(response => {
              if (response.success) {
                this.loggedIn = true;
                this.createEnquiry(resolve, reject);
              } else {
                this.userErrors = this.userErrorsFrom(response);
                reject();
              }
            });
          } else {
            this.createEnquiry(resolve, reject);
          }
        });
    },
    async checkEmail() {
      if (!this.user.email) return;

      this.existingAccount = !!(await userApi.checkGuestEmail(this.user.email));
    },
    async signInOrRegister() {
      await this.checkEmail();

      return this.existingAccount
        ? userApi.login(this.signInParams)
        : userApi.register(this.user);
    },
    userErrorsFrom(response) {
      if (this.existingAccount) return { password: [response.data.error] };

      return response.data.errors;
    },
    createEnquiry(resolve, reject) {
      businessApi
        .create(this.params)
        .then(() => {
          this.isFinished = true;
          this.sendAnalyticsEvent();
          resolve();
        })
        .catch(e => {
          this.errors = e.response.data.errors;
          reject();
        });
    },
    sendAnalyticsEvent() {
      analytics.track("Submitted B2B Form");
    }
  }
};
</script>
